import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CodeBlock } from "./CodeBlock";
import type { CodeSequenceProps, CodeBlockData } from "./types";

interface SortableBlockProps {
  block: CodeBlockData;
  onDelete: () => void;
  disabled?: boolean;
  isExecuting?: boolean;
}

/**
 * A sortable block within the sequence
 */
function SortableBlock({ block, onDelete, disabled, isExecuting }: SortableBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : isExecuting ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="touch-manipulation"
    >
      <CodeBlock
        block={block}
        isDragging={isDragging}
        onDelete={onDelete}
        disabled={disabled}
        showDelete={!disabled}
        isExecuting={isExecuting}
      />
    </div>
  );
}

/**
 * CodeSequence Component
 *
 * The drop zone and sortable container for the program's code blocks.
 * Shows the current sequence of commands that will be executed.
 * Styled as a "conveyor belt" or "assembly line" metaphor.
 */
export function CodeSequence({
  blocks,
  onReorder,
  onDelete,
  disabled = false,
  maxBlocks = 10,
  executingBlockId = null,
}: CodeSequenceProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: "sequence",
    disabled,
  });

  const isEmpty = blocks.length === 0;
  const isFull = blocks.length >= maxBlocks;

  return (
    <div
      ref={setNodeRef}
      className={`
        relative
        min-h-[200px]
        bg-gradient-to-br from-neutral-100 via-white to-neutral-50
        rounded-3xl
        p-5
        border-4
        ${isOver && !isFull
          ? "border-pixel-accent bg-pixel-accent/5 shadow-[0_0_24px_rgba(0,212,255,0.3)]"
          : "border-neutral-300"
        }
        ${isFull ? "border-fairy-300" : ""}
        shadow-[inset_0_4px_12px_rgba(0,0,0,0.05)]
        transition-all duration-200
      `}
      role="region"
      aria-label={`Program sequence, ${blocks.length} of ${maxBlocks} blocks`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="
            flex items-center justify-center
            w-10 h-10
            bg-gradient-to-br from-neutral-200 to-neutral-300
            rounded-xl
            border-2 border-neutral-400
            shadow-[0_2px_0_0_theme(colors.neutral.400)]
          "
        >
          <span className="text-xl">📋</span>
        </div>
        <h3
          className="
            font-display font-bold text-xl
            text-neutral-700
            tracking-wide
          "
        >
          My Program
        </h3>

        {/* Block counter */}
        <div className="flex-1" />
        <div
          className={`
            px-3 py-1.5
            rounded-full
            font-body font-semibold text-body-md
            ${isFull
              ? "bg-fairy-100 text-fairy-600 border border-fairy-300"
              : "bg-neutral-200 text-neutral-600 border border-neutral-300"
            }
          `}
        >
          {blocks.length}/{maxBlocks}
        </div>
      </div>

      {/* Conveyor belt decoration (top) */}
      <div className="relative mb-4">
        <div className="h-2 bg-neutral-300 rounded-full overflow-hidden">
          <div
            className={`
              h-full rounded-full
              transition-all duration-500
              ${blocks.length > 0
                ? "bg-gradient-to-r from-pixel-accent via-pixel to-pixel-accent"
                : "bg-neutral-400"
              }
            `}
            style={{ width: `${(blocks.length / maxBlocks) * 100}%` }}
          />
        </div>
        {/* Belt rollers */}
        <div className="absolute -top-1 left-2 w-4 h-4 rounded-full bg-neutral-400 border-2 border-neutral-500" />
        <div className="absolute -top-1 right-2 w-4 h-4 rounded-full bg-neutral-400 border-2 border-neutral-500" />
      </div>

      {/* Empty state */}
      {isEmpty && (
        <div
          className={`
            flex flex-col items-center justify-center
            py-12
            rounded-2xl
            border-4 border-dashed
            ${isOver ? "border-pixel-accent" : "border-neutral-300"}
            transition-colors duration-200
          `}
        >
          <div
            className={`
              text-5xl mb-4
              ${isOver ? "animate-bounce" : "animate-float"}
            `}
          >
            {isOver ? "📥" : "🤖"}
          </div>
          <p className="font-display font-semibold text-body-xl text-neutral-500 text-center">
            {isOver ? "Drop it here!" : "Drag blocks here to build!"}
          </p>
          <p className="font-body text-body-md text-neutral-400 text-center mt-1">
            Your robot will follow these commands
          </p>
        </div>
      )}

      {/* Sortable blocks list */}
      {!isEmpty && (
        <SortableContext
          items={blocks.map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-3">
            {blocks.map((block, index) => (
              <div key={block.id} className="relative">
                {/* Step number badge */}
                <div
                  className="
                    absolute -left-2 top-1/2 -translate-y-1/2 z-10
                    flex items-center justify-center
                    w-7 h-7
                    bg-gradient-to-br from-pixel to-pixel-dark
                    rounded-full
                    border-2 border-pixel-dark
                    shadow-[0_2px_4px_rgba(0,0,0,0.2)]
                  "
                >
                  <span className="font-display font-bold text-body-md text-white">
                    {index + 1}
                  </span>
                </div>

                {/* Block with indent for step number */}
                <div className="ml-6">
                  <SortableBlock
                    block={block}
                    onDelete={() => onDelete(block.id)}
                    disabled={disabled}
                    isExecuting={executingBlockId === block.id}
                  />
                </div>

                {/* Connector line between blocks */}
                {index < blocks.length - 1 && (
                  <div
                    className="
                      absolute left-[10px] top-full
                      w-0.5 h-3
                      bg-gradient-to-b from-pixel to-pixel-dark
                    "
                  />
                )}
              </div>
            ))}
          </div>
        </SortableContext>
      )}

      {/* Drop zone indicator when dragging over (and not empty) */}
      {!isEmpty && isOver && !isFull && (
        <div
          className="
            mt-3 py-4
            flex items-center justify-center
            rounded-2xl
            border-4 border-dashed border-pixel-accent
            bg-pixel-accent/10
            animate-pulse
          "
        >
          <span className="font-body font-semibold text-body-md text-pixel-accent">
            + Add here
          </span>
        </div>
      )}

      {/* Full indicator */}
      {isFull && (
        <div
          className="
            mt-3 py-3
            flex items-center justify-center gap-2
            rounded-xl
            bg-fairy-50
            border border-fairy-200
          "
        >
          <span className="text-lg">🎯</span>
          <span className="font-body font-semibold text-body-md text-fairy-600">
            Program is full!
          </span>
        </div>
      )}

      {/* Circuit decoration */}
      <div className="absolute bottom-3 left-3 opacity-15 pointer-events-none">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="15" stroke="currentColor" strokeWidth="2" className="text-neutral-400" />
          <circle cx="20" cy="20" r="8" stroke="currentColor" strokeWidth="2" className="text-neutral-400" />
          <circle cx="20" cy="20" r="3" fill="currentColor" className="text-neutral-400" />
          <path d="M20 5 V0 M20 35 V40 M5 20 H0 M35 20 H40" stroke="currentColor" strokeWidth="2" className="text-neutral-400" />
        </svg>
      </div>
    </div>
  );
}

export default CodeSequence;
